import type { InputSnapshot } from '../ReviewTab'
import './DataSourcesPanel.css'

interface VitalFormatter {
  label: string
  format: (v: number) => string
}

const CURRENCY = (v: number) => `$${Math.round(v).toLocaleString()}`
const PERCENT = (v: number) => `${(v * 100).toFixed(1)}%`
const RATIO = (v: number) => v.toFixed(2)
const COUNT = (v: number) => Math.round(v).toLocaleString()

const VITAL_FORMATTERS: Record<string, VitalFormatter> = {
  revenue: { label: 'Revenue', format: CURRENCY },
  gross_profit_margin: { label: 'Gross Profit Margin', format: PERCENT },
  net_profit_margin: { label: 'Net Profit Margin', format: PERCENT },
  revenue_per_employee: { label: 'Revenue per Employee', format: CURRENCY },
  current_ratio: { label: 'Asset-to-Liability Ratio', format: RATIO },
  customer_retention_rate: { label: 'Customer Retention Rate', format: PERCENT },
  avg_revenue_per_customer: { label: 'Avg Revenue per Customer', format: CURRENCY },
  employee_count: { label: 'Employee Count', format: COUNT },
  customer_count: { label: 'Customer Count', format: COUNT },
  order_count: { label: 'Orders', format: COUNT },
}

const VITAL_ORDER = [
  'revenue',
  'gross_profit_margin',
  'net_profit_margin',
  'revenue_per_employee',
  'current_ratio',
  'customer_retention_rate',
  'avg_revenue_per_customer',
  'employee_count',
  'customer_count',
  'order_count',
]

function formatCapturedAt(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    })
  } catch {
    return iso
  }
}

function vitalsProvenanceLabel(
  source: InputSnapshot['vitals']['source'],
  snapshotDate: string | null,
): string {
  if (source === 'period_bound') return '(period-scoped)'
  if (source === 'snapshot' && snapshotDate) return `(snapshot from ${snapshotDate})`
  if (source === 'mixed' && snapshotDate) return `(mixed — snapshot partial, from ${snapshotDate})`
  if (source === 'mixed') return '(mixed — snapshot partial)'
  return '(current fallback)'
}

function sessionSummary(sessions: InputSnapshot['strategy_data']['sessions']) {
  const messages = sessions.reduce((n, s) => n + (s.user_messages?.length || 0), 0)
  const completed = sessions.filter(s => s.completed).length
  const inProgress = sessions.length - completed
  return { messages, completed, inProgress }
}

function executionSummary(execution: Record<string, unknown>) {
  // Defensive: execution_dashboard's shape isn't formally typed. Look for
  // initiatives in the most common shapes and fall back to totals if unknown.
  const initiatives = Array.isArray((execution as any)?.initiatives)
    ? (execution as any).initiatives as Array<{ status?: string }>
    : null
  if (!initiatives) return { total: null as number | null, byStatus: {} as Record<string, number> }
  const byStatus: Record<string, number> = {}
  for (const init of initiatives) {
    const s = init?.status || 'unknown'
    byStatus[s] = (byStatus[s] || 0) + 1
  }
  return { total: initiatives.length, byStatus }
}

function statusBreakdown(byStatus: Record<string, number>): string {
  const parts: string[] = []
  const order = ['completed', 'complete', 'in_progress', 'stalled', 'not_started', 'unknown']
  const seen = new Set<string>()
  for (const k of order) {
    if (byStatus[k] !== undefined) {
      parts.push(`${byStatus[k]} ${k.replace('_', ' ')}`)
      seen.add(k)
    }
  }
  for (const k of Object.keys(byStatus)) {
    if (!seen.has(k)) parts.push(`${byStatus[k]} ${k.replace('_', ' ')}`)
  }
  return parts.join(', ')
}

interface Props {
  snapshot: InputSnapshot
}

export default function DataSourcesPanel({ snapshot }: Props) {
  const sessions = snapshot.strategy_data?.sessions || []
  const { messages, completed, inProgress } = sessionSummary(sessions)

  const vitalActuals = snapshot.vitals?.current_vitals?.actuals || {}
  const vitalsAsOf = snapshot.vitals?.as_of || snapshot.cycle?.end || ''

  const exec = executionSummary(snapshot.execution || {})

  return (
    <section className="data-sources-panel">
      <h4 className="dsp-title">Data Sources</h4>

      <div className="dsp-row">
        <div className="dsp-label">Strategy sessions</div>
        <div className="dsp-value">
          {snapshot.strategy_data.session_count} conversation
          {snapshot.strategy_data.session_count === 1 ? '' : 's'}, {messages} message
          {messages === 1 ? '' : 's'}
          <div className="dsp-sub">
            ({completed} completed, {inProgress} in progress)
          </div>
        </div>
      </div>

      <div className="dsp-row">
        <div className="dsp-label">Vitals near {vitalsAsOf}</div>
        <div className="dsp-value">
          <table className="dsp-vitals">
            <tbody>
              {VITAL_ORDER.map(key => {
                const v = vitalActuals[key]
                if (v === null || v === undefined || typeof v !== 'number') return null
                const fmt = VITAL_FORMATTERS[key]
                return (
                  <tr key={key}>
                    <td className="dsp-vital-label">{fmt.label}</td>
                    <td className="dsp-vital-value">{fmt.format(v)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="dsp-sub">
            {vitalsProvenanceLabel(snapshot.vitals.source, snapshot.vitals.snapshot_date)}
          </div>
        </div>
      </div>

      <div className="dsp-row">
        <div className="dsp-label">
          Execution <span className="dsp-caveat">(current-state at generation)</span>
        </div>
        <div className="dsp-value">
          {exec.total === null ? (
            <span className="dsp-placeholder">—</span>
          ) : (
            <>
              {exec.total} initiative{exec.total === 1 ? '' : 's'}
              {Object.keys(exec.byStatus).length > 0 && (
                <> · {statusBreakdown(exec.byStatus)}</>
              )}
            </>
          )}
        </div>
      </div>

      <div className="dsp-row">
        <div className="dsp-label">Prior reviews considered</div>
        <div className="dsp-value">{snapshot.prior_reviews?.length ?? 0}</div>
      </div>

      <div className="dsp-row">
        <div className="dsp-label">Snapshot captured</div>
        <div className="dsp-value">{formatCapturedAt(snapshot.captured_at)}</div>
      </div>
    </section>
  )
}
